import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { AlertTriangle } from 'lucide-react'
import { useAssignableUsers } from '../hooks'
import { useObject } from '@/features/objects/hooks'
import { useQuery } from '@tanstack/react-query'
import { objectsApi } from '@/features/objects/api'
import { locationsApi } from '@/features/locations/api'

interface AssignEngineerDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectID: string | null
    recoverRequestId: number | null
    waitGo: string | null
    analytics: number | null
    responsibleTeamId: number | null
    locationId: number | null
    onConfirm: (selectedUserId?: string | number | null) => void
    isLoading?: boolean
}

export function AssignEngineerDialog({
    open,
    onOpenChange,
    objectID,
    recoverRequestId,
    waitGo,
    analytics,
    responsibleTeamId,
    locationId,
    onConfirm,
    isLoading = false,
}: AssignEngineerDialogProps) {
    const { t } = useTranslation()
    const [showConfirmation, setShowConfirmation] = useState(false)
    const [selectedUserId, setSelectedUserId] = useState<string | number | null>(null)
    
    // Debug logging
    console.log('🔍 DEBUG AssignEngineerDialog:', {
        open,
        objectID,
        responsibleTeamId,
        locationId,
        responsibleTeamIdType: typeof responsibleTeamId,
        locationIdType: typeof locationId,
        responsibleTeamIdValue: responsibleTeamId,
        locationIdValue: locationId
    })
    
    const { data: users = [], isLoading: isLoadingUsers } = useAssignableUsers(objectID, responsibleTeamId, locationId)
    const { data: object, isLoading: isLoadingObject } = useObject(objectID)
    const isWait = waitGo?.toUpperCase() === 'WAIT'
    const hasAnalyticsWarning = analytics === 2

    // Fetch teams and technologies for display names
    const { data: teamsData } = useQuery({
        queryKey: ['teams'],
        queryFn: async () => {
            const response = await objectsApi.getTeams()
            return response.data?.data || response.data || []
        },
        enabled: !!objectID,
    })

    const { data: technologiesData } = useQuery({
        queryKey: ['technologies'],
        queryFn: async () => {
            const response = await objectsApi.getTechnologies()
            return response.data?.data || response.data || []
        },
        enabled: !!objectID,
    })

    const { data: locationsData } = useQuery({
        queryKey: ['locations'],
        queryFn: async () => {
            const response = await locationsApi.list({ page: 1, limit: 1000 })
            return response.data.data || []
        },
        enabled: !!objectID || !!locationId,
    })

    // Get team, technology, and location names
    // responsibleTeam might be a string (team name) or number (team ID)
    const teamName = object?.responsibleTeam
        ? (typeof object.responsibleTeam === 'number' && teamsData
            ? teamsData.find((t: any) => t.id === object.responsibleTeam)?.teamName || ''
            : typeof object.responsibleTeam === 'string'
            ? object.responsibleTeam
            : '')
        : ''
    const technologyName = object?.technology && technologiesData
        ? technologiesData.find((tech: any) => tech.id === object.technology)?.name || ''
        : ''
    const locationName = locationId && locationsData
        ? locationsData.find((loc: any) => loc.id === locationId)?.description || ''
        : ''

    const handleAssignClick = () => {
        if (hasAnalyticsWarning) {
            setShowConfirmation(true)
        } else {
            onConfirm(selectedUserId)
        }
    }

    const handleConfirm = () => {
        setShowConfirmation(false)
        onConfirm(selectedUserId)
    }

    // Reset selected user when dialog closes
    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setSelectedUserId(null)
        }
        onOpenChange(open)
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-[700px]">
                <DialogHeader>
                    <DialogTitle>{t('recoverRequests.assignEngineer')}</DialogTitle>
                    <DialogDescription className="sr-only">
                        {objectID ? t('recoverRequests.assignEngineerForObject', { objectID }) : t('recoverRequests.assignEngineerDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Object Information */}
                    {objectID && (
                        <div className="border rounded-md p-4 bg-muted/30 space-y-1.5">
                            <div className="text-sm space-y-1.5">
                                <div>
                                    <span className="text-sm font-medium">{objectID}</span>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {isLoadingObject ? 'Loading...' : (object?.objectDescription || '-')}
                                </div>
                                <div>
                                    <span className="font-medium">{t('recoverRequests.location')}:</span>{' '}
                                    <span className="text-muted-foreground">
                                        {locationName || '-'}
                                    </span>
                                </div>
                                <div>
                                    <span className="font-medium">{t('recoverRequests.responsibleTeam')}:</span>{' '}
                                    <span className="text-muted-foreground">
                                        {isLoadingObject ? 'Loading...' : (teamName || '-')}
                                    </span>
                                </div>
                                <div>
                                    <span className="font-medium">{t('recoverRequests.technology')}:</span>{' '}
                                    <span className="text-muted-foreground">
                                        {isLoadingObject ? 'Loading...' : (technologyName || '-')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="space-y-2">
                        <label className="text-sm font-medium leading-none">
                            {t('recoverRequests.selectEngineer')}
                        </label>
                        <div className="border rounded-md overflow-hidden">
                            {isLoadingUsers ? (
                                <div className="p-8 text-center text-sm text-muted-foreground">
                                    Loading...
                                </div>
                            ) : users.length === 0 ? (
                                <div className="p-8 text-center text-sm text-muted-foreground">
                                    {t('recoverRequests.noEngineersAvailable')}
                                </div>
                            ) : (
                                <RadioGroup
                                    value={selectedUserId?.toString() || ''}
                                    onValueChange={(value) => setSelectedUserId(value)}
                                    disabled={isLoading}
                                    className="w-full"
                                >
                                    <div className="overflow-y-auto max-h-[400px]">
                                        <table className="w-full">
                                            <thead className="sticky top-0 bg-muted border-b z-20">
                                                <tr>
                                                    <th className="text-left p-3 text-sm font-medium bg-muted">
                                                        {t('recoverRequests.user')}
                                                    </th>
                                                    <th className="text-left p-3 text-sm font-medium bg-muted">
                                                        {t('recoverRequests.location')}
                                                    </th>
                                                    <th className="text-left p-3 text-sm font-medium bg-muted">
                                                        {t('recoverRequests.primaryTeamName')}
                                                    </th>
                                                    <th className="text-left p-3 text-sm font-medium bg-muted">
                                                        {t('recoverRequests.otherTeams')}
                                                    </th>
                                                    <th className="text-left p-3 text-sm font-medium w-12 bg-muted">
                                                        <span className="sr-only">Select</span>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {users.map((user: any) => {
                                                    // Fields from SelectUser: id, name, email, PrimaryTeamName, OtherTeams, LocationId, PrimaryTeamId, SelectedTeamId
                                                    const userId = user.id
                                                    const userName = user.name || user.email || 'Unknown User'
                                                    const userLocationId = user.LocationId
                                                    const userLocationName = userLocationId && locationsData
                                                        ? locationsData.find((loc: any) => loc.id === userLocationId)?.description || ''
                                                        : ''
                                                    const primaryTeamName = user.PrimaryTeamName || ''
                                                    const otherTeams = user.OtherTeams || ''
                                                    
                                                    // Check if user's location and primary team match the object's location and responsible team
                                                    const userPrimaryTeamId = user.PrimaryTeamId
                                                    const locationMatches = locationId && userLocationId && Number(locationId) === Number(userLocationId)
                                                    const teamMatches = responsibleTeamId && userPrimaryTeamId && Number(responsibleTeamId) === Number(userPrimaryTeamId)
                                                    const isMatch = locationMatches && teamMatches
                                                    
                                                    return (
                                                        <tr
                                                            key={userId}
                                                            className={`border-b transition-colors cursor-pointer ${
                                                                isMatch 
                                                                    ? 'bg-[rgb(151,200,100)] hover:bg-[rgb(140,190,90)]' 
                                                                    : 'hover:bg-accent'
                                                            }`}
                                                            onClick={() => !isLoading && setSelectedUserId(userId)}
                                                        >
                                                            <td className="p-3">
                                                                <label
                                                                    htmlFor={`user-${userId}`}
                                                                    className="cursor-pointer text-sm"
                                                                >
                                                                    {userName}
                                                                </label>
                                                            </td>
                                                            <td className="p-3 text-sm text-muted-foreground">
                                                                {userLocationName || '-'}
                                                            </td>
                                                            <td className="p-3 text-sm text-muted-foreground">
                                                                {primaryTeamName}
                                                            </td>
                                                            <td className="p-3 text-sm text-muted-foreground">
                                                                {otherTeams}
                                                            </td>
                                                            <td className="p-3">
                                                                <RadioGroupItem
                                                                    value={userId?.toString() || ''}
                                                                    id={`user-${userId}`}
                                                                    className="cursor-pointer"
                                                                />
                                                            </td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </RadioGroup>
                            )}
                        </div>
                    </div>

                    {isWait && (
                        <Alert variant="destructive" className="[&>svg]:hidden">
                            <AlertDescription className="text-center">
                                <p>{t('recoverRequests.dependenciesNotComplete')}</p>
                            </AlertDescription>
                        </Alert>
                    )}

                    {hasAnalyticsWarning && (
                        <Alert variant="destructive" className="bg-destructive text-destructive-foreground [&>svg]:hidden">
                            <AlertDescription className="text-destructive-foreground text-center">
                                <div className="space-y-2">
                                    <p>{t('recoverRequests.potentialIssuesFound')}</p>
                                    <p>{t('recoverRequests.recommendIsolatedEnvironment')}</p>
                                </div>
                            </AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                        disabled={isLoading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleAssignClick}
                        disabled={isLoading || !selectedUserId}
                    >
                        {isLoading ? t('common.saving') : t('recoverRequests.assignEngineer')}
                    </Button>
                </DialogFooter>
            </DialogContent>

            {/* Confirmation Dialog for Analytics Warning */}
            <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>{t('recoverRequests.warning')}</DialogTitle>
                        <DialogDescription>
                            {t('recoverRequests.potentialIssuesFound')}
                        </DialogDescription>
                    </DialogHeader>

                    <Alert variant="destructive" className="mt-4 bg-destructive text-destructive-foreground [&>svg]:hidden">
                        <AlertDescription className="text-destructive-foreground text-center">
                            <div className="space-y-2">
                                <p>{t('recoverRequests.potentialIssuesFound')}</p>
                                <p>{t('recoverRequests.recommendIsolatedEnvironment')}</p>
                            </div>
                        </AlertDescription>
                    </Alert>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowConfirmation(false)}
                            disabled={isLoading}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleConfirm}
                            disabled={isLoading}
                        >
                            {t('recoverRequests.assignEngineer')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Dialog>
    )
}
